/* eslint-disable indent */
/* eslint-disable no-param-reassign */
import { useLocales } from '@/Locales';
import { uploadFile } from '@/services/common';
import { getShortUUID } from '@/utils';
import { getOutputFileName } from '@/utils/picControler/functions';
import { convert } from '@/utils/picControler/handler';
import { CompressFilesProps, ImageItem } from '@/utils/picControler/ImageBase';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Space, Tooltip } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useTransform, { createMessageData } from '../../hook/useTransform';
import { DefaultCompressOption } from '../../store/smallerAtoms';
import useHomeState from '../../store/useSmaller';
import ImageInput from '../ImageInput';
import styles from './index.less';

interface PicSmallerUploadProps {
  auto?: boolean; // 是否自动上传
  disabled?: boolean; // 是否禁用
  loading?: boolean; // 外置上传loading
  label?: string; // 上传提示
  tooltip?: string; // 提示信息
  uploadLabel?: string; // 上传提示
  accept?: string; // 接受文件类型
  uploadTip?: string; // 上传提示
  multiple?: boolean; // 是否多选
  maxFiles?: number; // 总的最大数量
  surpluCount?: number; // 剩余的数量
  maxSize?: number; // 最大文件大小
  duration?: number; // 视频时长
  dataNoDrag?: boolean; // 是否禁用拖拽
  border?: 'dashed' | 'solid'; // 边框样式
  enableDrag?: boolean; // 是否启用拖拽上传功能
  width?: number; // 宽度
  height?: number; // 高度
  fontSize?: number; // 字体大小
  className?: string; // 自定义类名
  onVideoUpload?: (file: Array<CompressFilesProps>) => void; // 视频上传回调
  onChange?: (urls: string[], file: CompressFilesProps[], fileLeng?: number) => void;
}

/**
 * @description: 图片压缩上传组件
 * @author: KaifengLi
 * @version: v2.25.0
 * @Date: 2024-11-01 14:17:32
 */
const PicSmallerUpload: React.FC<PicSmallerUploadProps> = (props) => {
  const {
    auto = true,
    loading = false,
    disabled = false,
    accept,
    label = '上传图片/视频',
    uploadLabel,
    uploadTip = '上传中',
    multiple = false,
    duration,
    dataNoDrag = false,
    maxFiles,
    surpluCount,
    maxSize,
    onChange,
    onVideoUpload,
    tooltip,
    border = 'solid',
    enableDrag = false, // 默认启用拖拽功能
    width = 100,
    height = 100,
    fontSize = 14,
    className,
  } = props;
  const { t } = useLocales();
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadAreaRef = useRef<HTMLDivElement>(null); // 上传区域引用
  const { option } = useHomeState(); // 压缩配置
  const { compressMessageImage } = useTransform(); // 压缩消息图片

  const [uploading, setUploading] = useState(false); // 上传loading
  const [videoUploadFiles, setVideoUploadFiles] = useState<Array<File>>([]); // 视频文件
  const [videoUpload, setVideoUpload] = useState(false); // 视频上传中
  const [tooltipVisible, setTooltipVisible] = useState<boolean | undefined>(undefined); // Tooltip 显示状态
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null); // Tooltip 恢复定时器
  const isFileDialogOpenRef = useRef(false); // 文件选择对话框是否打开

  // 上传视频
  const handleUploadVideo = (videoFiles: Array<File>) => {
    // setUploading(true);
    setVideoUpload(true);
    if (!videoFiles.length) {
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    }
    setVideoUploadFiles((prev) => [...prev, ...videoFiles]);

    const uploadPromises = videoFiles.map((file) => {
      const formData = new FormData();
      formData.append('file', file);
      return uploadFile(formData).then((res: any) => res);
    });

    return new Promise((resolve, reject) => {
      Promise.all(uploadPromises)
        .then((results) => {
          // console.log(results);
          // onChange?.(results, [], 'video');
          // setVideoUrls(results);)
          setVideoUploadFiles([]);
          resolve(results);
        })
        .catch((error) => {
          reject(error);
          console.error('Error uploading files:', error);
        })
        .finally(() => {
          setUploading(false);
          setVideoUpload(false);
        });
    });
  };

  // 文件上传
  const handleImageChange = useCallback(
    async (promise: Promise<ImageItem>[], videoFile?: Array<File>) => {
      const promiseResults = await Promise.all(promise);
      const uploadPromises: Promise<any>[] = [];
      const compressFiles: CompressFilesProps[] = [];
      // 视频文件直接上传，暂只有在单个时候支持
      let videoUrls: any = [];

      if (!onVideoUpload) {
        videoUrls = await handleUploadVideo(videoFile || []);
      }

      await Promise.all(
        promiseResults.map(async (item: ImageItem, index: number) => {
          const msg = createMessageData(item, option);
          if (item.blob.type !== 'image/bmp') {
            setUploading(true);
            const compressOutput = await convert(msg, 'compress');
            // console.log('压缩结果', compressOutput);
            if (compressOutput && compressOutput?.compress?.blob) {
              const list = compressMessageImage(item, compressOutput);
              if (list) {
                const fileName = getOutputFileName(list, DefaultCompressOption);
                const file = new File([compressOutput.compress.blob], fileName, {
                  type: compressOutput.compress.blob.type,
                });
                compressFiles.push({
                  file,
                  compress: compressOutput.compress,
                  sort: index,
                  new: true,
                  id: getShortUUID(),
                  type: 'image',
                });
                if (auto) {
                  const formDate = new FormData();
                  formDate.append('file', file);
                  uploadPromises.push(uploadFile(formDate));
                  // console.log(`文件 ${fileName} 的上传 Promise 已添加`);
                }
              }
            }
          } else {
            const file = new File([msg?.info.blob], msg?.info.name, {
              type: msg?.info.blob.type,
            });
            compressFiles.push({
              file,
              compress: {
                width: msg?.info.width,
                height: msg?.info.height,
                blob: msg?.info.blob,
                src: URL.createObjectURL(msg?.info.blob),
              },
              sort: index,
              id: getShortUUID(),
              type: 'image',
            });
          }
        }),
      );

      // 等待所有上传操作完成
      try {
        const res = await Promise.all(uploadPromises);
        const sortCompressFiles = compressFiles.sort((a, b) => a.sort - b.sort);
        if (onVideoUpload) {
          const index = sortCompressFiles.length;
          const videos: CompressFilesProps[] = videoFile?.length
            ? videoFile.map((el, i) => {
                return {
                  file: el,
                  compress: {
                    width: 0,
                    height: 0,
                    blob: el,
                    type: el.name,
                    src: URL.createObjectURL(el),
                  },
                  new: true,
                  sort: index + i + 1,
                  type: 'video',
                };
              })
            : [];

          console.log('所有文件压缩完成', { res, compressFiles, sortCompressFiles, videos });
          onChange?.(
            [],
            [...sortCompressFiles, ...videos],
            promise.length + (videoFile?.length || 0),
          );
          // if (videoFile?.length) {
          //   onVideoUpload?.();
          // }
        } else {
          onChange?.(
            res.length ? res : videoUrls,
            sortCompressFiles,
            promise.length + (videoUrls?.length || 0),
          );
        }
      } catch (error) {
        console.error('上传过程中发生错误:', error);
      } finally {
        setUploading(false);
      }
    },
    [auto, onChange, option, compressMessageImage],
  );

  useEffect(() => {
    setUploading(loading);
  }, [loading]);

  // 监听文件输入框的 focus 和 blur 事件，处理文件选择对话框打开/关闭时的 Tooltip 显示
  useEffect(() => {
    const inputElement = fileRef.current;
    if (!inputElement) {
      return undefined;
    }

    // 文件选择对话框打开时（focus 事件）
    const handleFocus = () => {
      isFileDialogOpenRef.current = true;
      setTooltipVisible(false);
    };

    // 文件选择对话框关闭时（blur 事件）
    const handleBlur = () => {
      isFileDialogOpenRef.current = false;
      // 延迟恢复 Tooltip 的正常行为，避免对话框关闭后立即显示
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      tooltipTimerRef.current = setTimeout(() => {
        setTooltipVisible(undefined);
      }, 100);
    };

    inputElement.addEventListener('focus', handleFocus);
    inputElement.addEventListener('blur', handleBlur);

    // eslint-disable-next-line consistent-return
    return () => {
      inputElement.removeEventListener('focus', handleFocus);
      inputElement.removeEventListener('blur', handleBlur);
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      {videoUploadFiles.length > 0 && (
        <div className={styles['pic-smaller-upload']} style={{ width, height }}>
          {videoUpload && (
            <Space direction="vertical">
              <LoadingOutlined style={{ fontSize: 18 }} />
              <div>{t('视频上传中')}...</div>
            </Space>
          )}
        </div>
      )}
      <Tooltip
        title={tooltip || label}
        trigger="hover"
        placement="top"
        open={tooltipVisible}
        onOpenChange={setTooltipVisible}
      >
        <div className={classNames('ocr-upload-btn', className)} data-no-drag={dataNoDrag}>
          <div
            ref={uploadAreaRef}
            className={classNames(
              styles['pic-smaller-upload'],
              disabled && styles.disabled,
              styles[border],
            )}
            style={{ width, height }}
            onClick={() => {
              if (uploading) {
                message.error(t('有任务处理中，请稍后'));
                return;
              }
              if (disabled) {
                return;
              }
              // 点击时隐藏 Tooltip
              setTooltipVisible(false);
              // 清除之前的定时器
              if (tooltipTimerRef.current) {
                clearTimeout(tooltipTimerRef.current);
              }
              fileRef.current?.click();
            }}
            onMouseLeave={() => {
              // 如果文件选择对话框未打开，鼠标离开时恢复 Tooltip 的正常行为
              if (!isFileDialogOpenRef.current) {
                setTooltipVisible(undefined);
              }
            }}
          >
            {uploading && (
              <Space size={4} direction="vertical">
                <LoadingOutlined style={{ fontSize: 18 }} />
                <div className={styles.uploadTip} style={{ fontSize }}>
                  {t(uploadTip)}...
                </div>
              </Space>
            )}
            {!uploading && (
              <Space size={4} direction="vertical">
                <PlusOutlined />
                <div className={styles.uploadLabel} style={{ fontSize }}>
                  {t(uploadLabel || label)}
                </div>
              </Space>
            )}
          </div>
          <ImageInput
            ref={fileRef}
            accept={accept}
            maxFiles={maxFiles}
            surpluCount={surpluCount}
            maxSize={maxSize}
            multiple={multiple}
            duration={duration}
            enableDrag={enableDrag}
            dragTarget={uploadAreaRef.current}
            onChange={handleImageChange}
          />
        </div>
      </Tooltip>
    </>
  );
};

export default PicSmallerUpload;
